import cn from 'classnames'
import type React from 'react'
import {
  type ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { useFormLayoutSideComponentDescribedBy } from '@/commons/context/FormLayoutSideComponentContext/FormLayoutSideComponentContext'
import { Button } from '@/design-system/Button/Button'
import { FieldFooter } from '@/design-system/common/FieldFooter/FieldFooter'
import type { RequiredIndicator } from '@/design-system/common/types'

import styles from './TextArea.module.scss'

/**
 * Props for the TextArea component.
 */
export type TextAreaProps = {
  /**
   * The name of the textarea field.
   */
  name: string
  /**
   * The initial number of visible text lines for the control. The field will still expand indefinitely if the input is higher than this value.
   * @default 7
   */
  initialRows?: number
  /**
   * The maximum number of characters allowed in the textarea.
   * @default 1000
   */
  maxLength?: number
  /**
   * Whether the field is optional.
   */
  required?: boolean
  /**
   * The label text for the textarea.
   */
  label: string | React.ReactNode
  /**
   * A description providing additional information about the textarea.
   */
  description?: string
  /**
   * Custom CSS class for the textarea component.
   */
  className?: string
  /**
   * Whether the textarea is disabled.
   */
  disabled?: boolean
  hasDefaultPlaceholder?: boolean
  /** What type of required indicator is displayed */
  requiredIndicator?: RequiredIndicator
  /**
   * Error text displayed under the field. If the error is trythy, the field has the error styles.
   */
  error?: string
  onChange?: (e: { target: { value: string; name?: string } }) => void
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  value?: string
  describedBy?: string
} & (
  | {
      /**
       * Whether the template button should be displayed.
       */
      hasTemplateButton: boolean
      /**
       * Content of the templated added to the field when the template button is clicked
       */
      wordingTemplate: string
      /**
       * Callback after the template button is clicked.
       */
      onPressTemplateButton: () => void
    }
  | {
      hasTemplateButton?: false
      wordingTemplate?: never
      onPressTemplateButton?: never
    }
)

/**
 * @param {TextAreaProps} props - The props for the TextArea component.
 * @returns {JSX.Element} The rendered TextArea component.
 *
 * @example
 * <TextArea
 *   name="message"
 *   label="Your Message"
 *   description="Please enter your message."
 *   maxLength={500}
 * />
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      name,
      className,
      disabled,
      description,
      label,
      maxLength = 1000,
      required = false,
      requiredIndicator = 'symbol',
      initialRows = 7,
      hasTemplateButton = false,
      wordingTemplate,
      hasDefaultPlaceholder,
      onPressTemplateButton,
      error,
      onChange,
      onBlur,
      value,
      describedBy,
    }: TextAreaProps,
    ref: ForwardedRef<HTMLTextAreaElement>
  ) => {
    const formLayoutDescribedBy = useFormLayoutSideComponentDescribedBy()
    const textAreaRef = useRef<HTMLTextAreaElement>(null)

    const [textValue, setTextValue] = useState(value)

    const fieldId = useId()
    const descriptionId = useId()
    const charactersCountId = useId()
    const errorId = useId()

    const countValue =
      textAreaRef.current?.value?.length ?? textValue?.length ?? 0

    useImperativeHandle(ref, () => textAreaRef.current as HTMLTextAreaElement)

    /**
     * Updates the height of the textarea based on its content.
     * Resets the height to initial value before setting it to scrollHeight to allow shrinking.
     */
    const updateTextAreaHeight = useCallback(() => {
      if (textAreaRef.current) {
        // Reset the textarea height to its initial value before reading the scrollHeight
        textAreaRef.current.style.height = 'unset'

        const scrollHeight = textAreaRef.current.scrollHeight
        if (scrollHeight > 0) {
          textAreaRef.current.style.height = `${hasTemplateButton ? scrollHeight + 92 : scrollHeight}px`
        }
      }
    }, [hasTemplateButton])

    useEffect(() => {
      // Set the textarea height after the first render to fit the initial content
      updateTextAreaHeight()
    }, [updateTextAreaHeight])

    const describedByIds = [
      description ? descriptionId : '',
      error ? errorId : '',
      charactersCountId,
      describedBy ?? '',
      formLayoutDescribedBy ?? '',
    ]
      .filter(Boolean)
      .join(' ')

    const generateTemplate = () => {
      wordingTemplate && setTextValue(wordingTemplate)
      if (textAreaRef.current) {
        textAreaRef.current.focus()
        textAreaRef.current.setSelectionRange(128, 128)
      }
      onPressTemplateButton?.()
    }

    return (
      <div className={className}>
        <div>
          <label
            className={cn(styles['label'], {
              [styles['has-description']]: Boolean(description),
            })}
            htmlFor={fieldId}
          >
            {label}
            {required && requiredIndicator === 'symbol' && <>&nbsp;*</>}
            {required && requiredIndicator === 'explicit' && (
              <span className={styles['field-header-right']}>Obligatoire</span>
            )}
          </label>
          {description && (
            <p
              id={descriptionId}
              data-testid={`description-${name}`}
              className={styles['description']}
            >
              {description}
            </p>
          )}
        </div>
        <div className={styles['wrapper']}>
          <textarea
            ref={textAreaRef}
            aria-invalid={!!error}
            aria-describedby={describedByIds || undefined}
            className={cn(styles['text-area'], {
              [styles['has-error']]: !!error,
            })}
            disabled={disabled}
            id={fieldId}
            rows={initialRows}
            value={textValue}
            maxLength={maxLength}
            aria-required={!required}
            placeholder={hasDefaultPlaceholder ? 'Écrivez ici...' : undefined}
            onChange={(event) => {
              setTextValue(event.target.value)
              if (onChange) {
                onChange({
                  ...event,
                  target: { ...event.target, value: event.target.value, name },
                })
              }
            }}
            onBlur={(event) => {
              setTextValue(event.target.value)
              if (onBlur) {
                onBlur({
                  ...event,
                  target: { ...event.target, value: event.target.value, name },
                })
              }
            }}
          />
          {hasTemplateButton && (
            <div className={styles['template-button']}>
              <Button
                onClick={generateTemplate}
                disabled={Boolean(textValue?.length)}
                label="Générer un modèle"
              />
            </div>
          )}
        </div>
        <FieldFooter
          error={error}
          errorId={errorId}
          charactersCount={{ current: countValue, max: maxLength }}
          charactersCountId={charactersCountId}
        />
      </div>
    )
  }
)

TextArea.displayName = 'TextArea'
